import {
  SimProgressLesson,
  SimProgressRating,
} from '@yaak/components/services/api/api'

export type SimProgressLessonStatus =
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'open'
  | 'locked'

export type SimProgressStatusFilter = 'all' | SimProgressLessonStatus

export const SIM_PROGRESS_STATUS_FILTERS: SimProgressStatusFilter[] = [
  'all',
  'in_progress',
  'completed',
  'failed',
  'open',
  'locked',
]

export const SIM_PROGRESS_LESSON_STATUSES: SimProgressLessonStatus[] = [
  'completed',
  'failed',
  'in_progress',
  'open',
  'locked',
]

export const getSimProgressLessonStatus = (
  lesson: Pick<SimProgressLesson, 'inProgress' | 'result' | 'unlocked'>
): SimProgressLessonStatus => {
  if (lesson.inProgress) {
    return 'in_progress'
  }
  if (lesson.result === 'SUCCESS') {
    return 'completed'
  }
  if (lesson.result === 'FAIL') {
    return 'failed'
  }
  if (lesson.unlocked) {
    return 'open'
  }
  return 'locked'
}

export const getSimProgressLessonCategory = (
  lesson: Pick<SimProgressLesson, 'group' | 'type'>
): string => lesson.group?.title || lesson.type || '—'

export const sortSimProgressLessons = (
  lessons: SimProgressLesson[]
): SimProgressLesson[] =>
  [...lessons].sort((a, b) => a.displayIndex - b.displayIndex)

export const filterSimProgressLessons = (
  lessons: SimProgressLesson[],
  status: SimProgressStatusFilter
): SimProgressLesson[] => {
  const sorted = sortSimProgressLessons(lessons)
  if (status === 'all') {
    return sorted
  }
  return sorted.filter((lesson) => getSimProgressLessonStatus(lesson) === status)
}

export type SimProgressRatingEntry = {
  category: string
  count: number
}

export const getSimProgressRatingEntries = (
  rating: SimProgressRating | null
): SimProgressRatingEntry[] =>
  Object.entries(rating || {})
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category))

export const getSimProgressRatingTotal = (
  rating: SimProgressRating | null
): number | null =>
  rating ? Object.values(rating).reduce((sum, count) => sum + count, 0) : null

export const formatSimProgressRatingCategory = (category: string): string => {
  const words = category.toLowerCase().split('_').filter(Boolean).join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const countSimProgressLessonStatuses = (
  lessons: SimProgressLesson[]
): Record<SimProgressLessonStatus, number> => {
  const counts: Record<SimProgressLessonStatus, number> = {
    completed: 0,
    failed: 0,
    in_progress: 0,
    open: 0,
    locked: 0,
  }
  lessons.forEach((lesson) => {
    counts[getSimProgressLessonStatus(lesson)]++
  })
  return counts
}
